export interface VisualViewportSource {
  readonly height: number;
  /** Pinch-zoom factor; 1 when the page is not zoomed. */
  readonly scale: number;
  addEventListener(type: "resize" | "scroll", listener: () => void): void;
  removeEventListener(type: "resize" | "scroll", listener: () => void): void;
}

/** The part of `window` that owns the document scroll position. */
export interface DocumentScroller {
  readonly scrollX: number;
  readonly scrollY: number;
  scrollTo(x: number, y: number): void;
}

const VISUAL_VIEWPORT_HEIGHT_PROPERTY = "--forge-visual-viewport-height";

/** Scale values this close to 1 are "not zoomed"; some engines report 0.9999. */
const UNZOOMED_SCALE_TOLERANCE = 0.01;

/**
 * Keeps the app shell exactly one visual viewport tall.
 *
 * A software keyboard on iOS Safari (and on Android Chrome unless the page opts
 * into `interactive-widget=resizes-content`) leaves the layout viewport alone
 * and shrinks only the visual viewport. `svh`/`dvh` do not move, so the shell
 * reads the live height from here instead and `index.css` sizes itself from
 * the custom property.
 *
 * The same engines reveal a focused field by panning the window, and Safari
 * does not always pan back when the keyboard closes: the bar ends up under the
 * browser chrome with a strip of page background below the shell. This app's
 * document never scrolls (`html`, `body`, `#root` are `overflow: hidden` and
 * viewport-sized), so any window scroll offset is that stray pan and is undone
 * on the next viewport event. By then the shell has already been resized to the
 * visual viewport, so the focused field is inside it and the browser has no
 * reason to pan again.
 *
 * Pinch zoom also shrinks the visual viewport. That is the person's zoom, not
 * a keyboard: the shell must not reflow underneath it and their pan must be
 * left alone, so while zoomed the property is cleared (the `svh` fallback takes
 * over) and no scroll is touched.
 */
export function installVisualViewportHeightSync(input: {
  readonly root: HTMLElement;
  readonly viewport: VisualViewportSource | null;
  readonly scroller: DocumentScroller;
}): () => void {
  if (input.viewport === null) return () => {};

  const { viewport, scroller, root } = input;
  const sync = () => {
    if (Math.abs(viewport.scale - 1) > UNZOOMED_SCALE_TOLERANCE) {
      root.style.removeProperty(VISUAL_VIEWPORT_HEIGHT_PROPERTY);
      return;
    }
    root.style.setProperty(VISUAL_VIEWPORT_HEIGHT_PROPERTY, `${Math.round(viewport.height)}px`);
    if (scroller.scrollX !== 0 || scroller.scrollY !== 0) {
      scroller.scrollTo(0, 0);
    }
  };

  sync();
  viewport.addEventListener("resize", sync);
  viewport.addEventListener("scroll", sync);

  return () => {
    viewport.removeEventListener("resize", sync);
    viewport.removeEventListener("scroll", sync);
    root.style.removeProperty(VISUAL_VIEWPORT_HEIGHT_PROPERTY);
  };
}
